// ProfileLivestreamItem.jsx
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { format, fromUnixTime } from 'date-fns';
import { withTranslation } from 'react-i18next';

import { SanitizedLink as Link } from '../component/SanitizedLink.jsx';

import { color, textColor } from '../style/variables.js';

import numberWithCommas from '../resource/numberWithCommas.js';
import { ProfileListType } from '../resource/profileConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { SESSION_DIAMOND_FIELD } from '../resource/liveStreamConstants.js';
import { EMPTY_OBJECT } from '../resource/defaults.js';

import LiveIconSource from '../../img/btn_live_report.svg';
import DiamondIconSource from '../../img/ic_diamond.svg';

export const ProfileLivestreamItem = ({
  sessionId = null,
  startUnixTimestamp = null,
  endUnixTimestamp = null,
  summary = EMPTY_OBJECT,
  t,
}) => {
  const isDurationProcessing = !startUnixTimestamp || !endUnixTimestamp;

  const durationString = useMemo(() => {
    if (isDurationProcessing)
      return t('general_processing', {
        ns: TranslationNamespace.PROFILE,
      });

    const diffInSeconds = endUnixTimestamp - startUnixTimestamp;
    const hours = Math.floor(diffInSeconds / 3600);
    const minutes = Math.floor((diffInSeconds % 3600) / 60);
    const seconds = diffInSeconds % 60;

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }, [endUnixTimestamp, isDurationProcessing, startUnixTimestamp, t]);

  return (
    <StyledProfileTabLivestreamItem
      to={`/me/${ProfileListType.LIVESTREAM_SESSION}/${sessionId}`}
    >
      <LivestreamIcon>
        <img src={LiveIconSource} alt="livestream icon" />
      </LivestreamIcon>
      <StartTime>
        {startUnixTimestamp
          ? format(fromUnixTime(startUnixTimestamp), 'yyyy/MM/dd HH:mm')
          : '-'}
      </StartTime>
      <DurationTitle>
        {t('title_stream_duration', {
          ns: TranslationNamespace.PROFILE,
        })}
      </DurationTitle>
      <Duration isDurationProcessing={isDurationProcessing}>
        {durationString}
      </Duration>
      <DiamondTitle>
        {t('title_stream_total_diamond', {
          ns: TranslationNamespace.PROFILE,
        })}
      </DiamondTitle>
      <Diamond>
        <img src={DiamondIconSource} alt="diamond" />
        {numberWithCommas({
          number: Object.keys(SESSION_DIAMOND_FIELD).reduce(
            (accumulator, key) => accumulator + (summary[key] || 0),
            0
          ),
        })}
      </Diamond>
    </StyledProfileTabLivestreamItem>
  );
};

ProfileLivestreamItem.propTypes = {
  sessionId: PropTypes.string,
  startUnixTimestamp: PropTypes.number,
  endUnixTimestamp: PropTypes.number,
  summary: PropTypes.object,
  t: PropTypes.func.isRequired,
};

const StyledProfileTabLivestreamItem = styled(Link)`
  width: 100%;
  height: 88px;
  display: grid;
  grid-template-areas:
    'icon start-time start-time'
    'icon duration-title duration'
    'icon diamond-title diamond';
  grid-template-columns: 60px auto auto;
  grid-template-rows: 14px 12px 16px;
  grid-column-gap: 12px;
  grid-row-gap: 8px;
  align-content: center;
  & + & {
    border-top: 1px solid rgba(100, 100, 100, 0.08);
  }
`;

const LivestreamIcon = styled.div`
  border-radius: 6px;
  width: 60px;
  height: 60px;
  grid-area: icon;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${color.grey['900']};
  > img {
    width: 48px;
    height: 48px;
    object-fit: contain;
  }
`;

const StartTime = styled.div`
  grid-area: start-time;
  font-size: 14px;
  line-height: 1;
  color: ${textColor.white};
`;

const DurationTitle = styled.div`
  grid-area: duration-title;
  font-size: 12px;
  line-height: 1;
  color: ${textColor.black};
`;

const DiamondTitle = styled.div`
  grid-area: diamond-title;
  font-size: 12px;
  line-height: 1;
  color: ${textColor.black};
`;

const Duration = styled.div.attrs(({ isDurationProcessing }) => ({
  style: {
    color: isDurationProcessing ? 'rgba(200, 200, 200, 0.5)' : textColor.white,
  },
}))`
  grid-area: duration;
  font-size: 12px;
  line-height: 1;
  text-align: right;
`;

const Diamond = styled.div`
  grid-area: diamond;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  font-size: 12px;
  line-height: 1;
  color: ${textColor.primary};
  > img {
    width: 16px;
    height: 16px;
    margin-right: 4px;
  }
`;

export default withTranslation()(ProfileLivestreamItem);
